// Seasonal / holiday calendar utility
// Returns the currently active holiday and the next upcoming one
// so any page can show contextual suggestions without an API call.

export const HOLIDAYS = [
  {
    id: 'new_year',
    name: "New Year's",
    emoji: '🎆',
    color: '#0ea5e9',
    gradient: 'linear-gradient(135deg,#0284c7,#7c3aed)',
    bg: '#f0f9ff',
    // window when banner shows (inclusive, month is 1-based)
    windowStart: { month: 12, day: 26 },
    windowEnd:   { month: 1,  day: 5 },
    // actual holiday date (used for "X days away" label)
    holiday: { month: 1, day: 1 },
    description: "Ring in the new year with a magical story about fresh starts and big dreams.",
    themes: ['🎆 New Year', 'Hope', 'New Beginnings'],
    tone: 'magical',
    suggestedPrompt: "A magical New Year's Eve adventure where the child helps the clock tower ring in the new year.",
  },
  {
    id: 'lunar_new_year',
    name: 'Lunar New Year',
    emoji: '🧧',
    color: '#dc2626',
    gradient: 'linear-gradient(135deg,#dc2626,#d97706)',
    bg: '#fef2f2',
    windowStart: { month: 2, day: 3 },
    windowEnd:   { month: 2, day: 24 },
    holiday: { month: 2, day: 17 },   // Feb 17 2026
    description: 'Celebrate the Lunar New Year with a story full of lanterns, dragons, and lucky red envelopes.',
    themes: ['Lunar New Year', 'Family', 'Gratitude'],
    tone: 'magical',
    suggestedPrompt: 'A Lunar New Year celebration where the child joins a dragon parade and discovers the secret of the lucky red envelope.',
  },
  {
    id: 'valentines',
    name: "Valentine's Day",
    emoji: '💝',
    color: '#e11d48',
    gradient: 'linear-gradient(135deg,#e11d48,#f472b6)',
    bg: '#fff1f2',
    windowStart: { month: 2, day: 7 },
    windowEnd:   { month: 2, day: 14 },
    holiday: { month: 2, day: 14 },
    description: "Warm hearts with a story about friendship, love, and acts of kindness.",
    themes: ['Kindness', 'Friendship', 'Love'],
    tone: 'cozy',
    suggestedPrompt: "A Valentine's Day story where the child crafts magical cards that make everyone in town feel special.",
  },
  {
    id: 'st_patricks',
    name: "St. Patrick's Day",
    emoji: '☘️',
    color: '#16a34a',
    gradient: 'linear-gradient(135deg,#15803d,#4ade80)',
    bg: '#f0fdf4',
    windowStart: { month: 3, day: 12 },
    windowEnd:   { month: 3, day: 17 },
    holiday: { month: 3, day: 17 },
    description: "Follow the rainbow and outsmart a mischievous leprechaun on a lucky adventure.",
    themes: ['Adventure', 'Bravery'],
    tone: 'funny',
    suggestedPrompt: "A St. Patrick's Day adventure where the child chases a leprechaun across Ireland to find the pot of gold.",
  },
  {
    id: 'eid',
    name: 'Eid',
    emoji: '🌙',
    color: '#059669',
    gradient: 'linear-gradient(135deg,#047857,#d97706)',
    bg: '#f0fdf4',
    windowStart: { month: 3, day: 14 },
    windowEnd:   { month: 3, day: 26 },
    holiday: { month: 3, day: 20 },   // Eid al-Fitr Mar 20 2026
    description: "Celebrate Eid with a joyful story about family, feasting, and the spirit of giving.",
    themes: ['Family', 'Generosity', 'Gratitude'],
    tone: 'cozy',
    suggestedPrompt: "An Eid al-Fitr story where the child helps prepare a feast and shares gifts with neighbours, discovering the true meaning of celebration.",
  },
  {
    id: 'easter',
    name: 'Easter',
    emoji: '🐣',
    color: '#7c3aed',
    gradient: 'linear-gradient(135deg,#7c3aed,#f472b6)',
    bg: '#f5f3ff',
    windowStart: { month: 3, day: 24 },
    windowEnd:   { month: 4, day: 12 },
    holiday: { month: 4, day: 5 },    // Apr 5 2026
    description: "A springtime tale of Easter eggs, fluffy bunnies, and the magic of new beginnings.",
    themes: ['Easter', 'Kindness', 'Spring'],
    tone: 'magical',
    suggestedPrompt: "An Easter morning story where the child helps the Easter Bunny hide eggs for all the animals in the Enchanted Forest.",
  },
  {
    id: 'mothers_day',
    name: "Mother's Day",
    emoji: '💐',
    color: '#db2777',
    gradient: 'linear-gradient(135deg,#db2777,#f9a8d4)',
    bg: '#fdf2f8',
    windowStart: { month: 5, day: 4 },
    windowEnd:   { month: 5, day: 11 },
    holiday: { month: 5, day: 10 },   // 2nd Sunday of May 2026
    description: "A sweet story celebrating mums — the real-life superheroes of every family.",
    themes: ['Gratitude', 'Love', 'Kindness'],
    tone: 'cozy',
    suggestedPrompt: "A Mother's Day story where the child goes on a magical quest to create the perfect surprise gift for Mum.",
  },
  {
    id: 'fathers_day',
    name: "Father's Day",
    emoji: '👨‍👧',
    color: '#2563eb',
    gradient: 'linear-gradient(135deg,#1d4ed8,#0ea5e9)',
    bg: '#eff6ff',
    windowStart: { month: 6, day: 14 },
    windowEnd:   { month: 6, day: 22 },
    holiday: { month: 6, day: 21 },   // 3rd Sunday of June 2026
    description: "An adventure story starring Dad — the bravest, silliest hero in the world.",
    themes: ['Bravery', 'Family', 'Adventure'],
    tone: 'adventurous',
    suggestedPrompt: "A Father's Day adventure where the child and Dad team up to rescue a friendly dragon and save their magical village.",
  },
  {
    id: 'diwali',
    name: 'Diwali',
    emoji: '🪔',
    color: '#d97706',
    gradient: 'linear-gradient(135deg,#b45309,#f59e0b)',
    bg: '#fffbeb',
    windowStart: { month: 10, day: 1 },
    windowEnd:   { month: 10, day: 17 },
    holiday: { month: 10, day: 8 },    // Oct 8 2026
    description: "Celebrate the Festival of Lights with a dazzling story of triumph and joy.",
    themes: ['Diwali', 'Bravery', 'Kindness'],
    tone: 'magical',
    suggestedPrompt: "A Diwali story where the child lights the first diya to guide the spirits of light back to their village, defeating the darkness.",
  },
  {
    id: 'halloween',
    name: 'Halloween',
    emoji: '🎃',
    color: '#ea580c',
    gradient: 'linear-gradient(135deg,#c2410c,#7c3aed)',
    bg: '#fff7ed',
    windowStart: { month: 10, day: 25 },
    windowEnd:   { month: 11, day: 1 },
    holiday: { month: 10, day: 31 },
    description: "A mildly spooky (age-safe!) trick-or-treat adventure full of silly monsters and candy corn.",
    themes: ['🎃 Halloween', 'Bravery', 'Friendship'],
    tone: 'spooky',
    suggestedPrompt: "A Halloween adventure where the child discovers that the scariest house on the street is actually home to the kindest witch in the neighbourhood.",
  },
  {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    emoji: '🦃',
    color: '#b45309',
    gradient: 'linear-gradient(135deg,#92400e,#d97706)',
    bg: '#fffbeb',
    windowStart: { month: 11, day: 19 },
    windowEnd:   { month: 11, day: 27 },
    holiday: { month: 11, day: 26 },   // 4th Thursday Nov 2026
    description: "A warm story about gratitude, sharing a feast, and everything worth being thankful for.",
    themes: ['Gratitude', 'Kindness', 'Family'],
    tone: 'cozy',
    suggestedPrompt: "A Thanksgiving story where the child and a grateful turkey team up to cook the most magical feast the forest has ever seen.",
  },
  {
    id: 'hanukkah',
    name: 'Hanukkah',
    emoji: '🕎',
    color: '#1d4ed8',
    gradient: 'linear-gradient(135deg,#1d4ed8,#60a5fa)',
    bg: '#eff6ff',
    windowStart: { month: 11, day: 29 },
    windowEnd:   { month: 12, day: 12 },
    holiday: { month: 12, day: 4 },    // Hanukkah starts Dec 4 2026
    description: "Eight nights of miracles! A Festival of Lights story with spinning dreidels and golden gelt.",
    themes: ['Hanukkah', 'Bravery', 'Family'],
    tone: 'magical',
    suggestedPrompt: "A Hanukkah adventure where the child discovers a magical menorah whose flames each reveal a different act of courage from the past.",
  },
  {
    id: 'christmas',
    name: 'Christmas',
    emoji: '🎄',
    color: '#dc2626',
    gradient: 'linear-gradient(135deg,#dc2626,#16a34a)',
    bg: '#fef2f2',
    windowStart: { month: 12, day: 11 },
    windowEnd:   { month: 12, day: 31 },
    holiday: { month: 12, day: 25 },
    description: "Santa, reindeer, and the magic of giving — the perfect Christmas Eve bedtime story.",
    themes: ['🎄 Christmas', 'Generosity', 'Kindness'],
    tone: 'magical',
    suggestedPrompt: "A Christmas Eve adventure where the child helps Santa rescue a lost reindeer and saves Christmas for the whole village.",
  },
];

/**
 * Returns today's seasonal context.
 * @param {Date} [date=new Date()] — override for testing
 * @returns {{ active: Holiday|null, upcoming: { holiday: Holiday, daysAway: number }|null }}
 */
export function getSeasonalContext(date = new Date()) {
  const m = date.getMonth() + 1;   // 1-12
  const d = date.getDate();

  const inWindow = (holiday, m, d) => {
    const { windowStart: s, windowEnd: e } = holiday;
    // Handle year-boundary windows (e.g. Dec 26 – Jan 5)
    if (s.month > e.month) {
      return (m > s.month || (m === s.month && d >= s.day)) ||
             (m < e.month || (m === e.month && d <= e.day));
    }
    if (m < s.month || m > e.month) return false;
    if (m === s.month && d < s.day) return false;
    if (m === e.month && d > e.day) return false;
    return true;
  };

  const daysUntil = (holiday, refDate) => {
    const year = refDate.getFullYear();
    let target = new Date(year, holiday.holiday.month - 1, holiday.holiday.day);
    if (target < refDate) target = new Date(year + 1, holiday.holiday.month - 1, holiday.holiday.day);
    return Math.ceil((target - refDate) / (1000 * 60 * 60 * 24));
  };

  // Find active holiday (highest priority = last matching in array wins, override with first)
  const active = HOLIDAYS.find(h => inWindow(h, m, d)) || null;

  // Find next upcoming holiday within 30 days (not already active)
  let upcoming = null;
  for (const h of HOLIDAYS) {
    if (active && h.id === active.id) continue;
    const days = daysUntil(h, date);
    if (days > 0 && days <= 30) {
      if (!upcoming || days < upcoming.daysAway) {
        upcoming = { holiday: h, daysAway: days };
      }
    }
  }

  return { active, upcoming };
}
